import { Injectable } from '@angular/core';

interface PerformanceSummary {
  timestamp: number;
  marks: { [key: string]: number };
  durations: { [key: string]: number };
}

/**
 * Performance tracking service that measures WebSocket connection time
 * and exports timing data for Playwright MCP tests
 */
@Injectable({ providedIn: 'root' })
export class PerformanceTrackerService {
  private timings: Map<string, number> = new Map();

  constructor() {
    // Mark page load start
    if (typeof performance !== 'undefined') {
      this.mark('page-load-start');
    }

    // Export timing API to window for Playwright access
    this.exportToWindow();
  }

  /**
   * Mark a performance milestone
   */
  mark(label: string): void {
    const timestamp = performance.now();
    this.timings.set(label, timestamp);

    // Also use native Performance API
    try {
      performance.mark(label);
    } catch (e) {
      console.warn(`Failed to mark performance: ${label}`, e);
    }

    console.log(`[Performance] ${label}: ${timestamp.toFixed(2)}ms since page load`);
  }

  /**
   * Measure duration between two marks
   */
  measure(name: string, startMark: string, endMark?: string): number | null {
    if (!endMark) {
      endMark = `${name}-end`;
      this.mark(endMark);
    }

    try {
      performance.measure(name, startMark, endMark);
      const measure = performance.getEntriesByName(name)[0];
      const duration = measure ? measure.duration : 0;
      console.log(`[Performance] ${name}: ${duration.toFixed(2)}ms`);
      return duration;
    } catch (e) {
      console.warn(`Failed to measure: ${name}`, e);

      // Fallback calculation
      const start = this.timings.get(startMark);
      const end = this.timings.get(endMark);
      if (start !== undefined && end !== undefined) {
        const duration = end - start;
        console.log(`[Performance] ${name} (fallback): ${duration.toFixed(2)}ms`);
        return duration;
      }
      return null;
    }
  }

  /**
   * Get current timing summary
   */
  getSummary(): PerformanceSummary {
    const summary: PerformanceSummary = {
      timestamp: Date.now(),
      marks: {},
      durations: {}
    };

    // Export all marks
    this.timings.forEach((time, label) => {
      summary.marks[label] = time;
    });

    // Calculate key durations
    const marks = Array.from(this.timings.keys());

    if (marks.includes('page-load-start') && marks.includes('angular-bootstrap-complete')) {
      summary.durations['angular-bootstrap'] = this.measure('angular-bootstrap', 'page-load-start', 'angular-bootstrap-complete') || 0;
    }

    if (marks.includes('angular-bootstrap-complete') && marks.includes('auth-complete')) {
      summary.durations['auth-processing'] = this.measure('auth-processing', 'angular-bootstrap-complete', 'auth-complete') || 0;
    }

    // 🎯 KEY METRIC: Time to interactive UI (what the user actually experiences)
    if (marks.includes('page-load-start') && marks.includes('auth-complete')) {
      summary.durations['time-to-interactive'] = this.measure('time-to-interactive', 'page-load-start', 'auth-complete') || 0;
    }

    // Background connection time (doesn't block user interaction)
    if (marks.includes('auth-complete') && marks.includes('websocket-connected')) {
      summary.durations['websocket-connection'] = this.measure('websocket-connection', 'auth-complete', 'websocket-connected') || 0;
    }

    if (marks.includes('page-load-start') && marks.includes('websocket-connected')) {
      summary.durations['total-connection-time'] = this.measure('total-connection-time', 'page-load-start', 'websocket-connected') || 0;
    }

    return summary;
  }

  /**
   * Export timing data to window for Playwright access
   */
  private exportToWindow(): void {
    if (typeof window !== 'undefined') {
      (window as any).performanceTimings = {
        getMark: (label: string) => this.timings.get(label),
        getAllMarks: () => Object.fromEntries(this.timings),
        getSummary: () => this.getSummary(),
        getPerformanceEntries: () => performance.getEntriesByType('mark'),
        getMeasures: () => performance.getEntriesByType('measure')
      };
    }
  }

  /**
   * Log final performance summary to console
   */
  logSummary(): void {
    const summary = this.getSummary();

    console.log('╔════════════════════════════════════════════════════════════╗');
    console.log('║              Application Performance Summary                ║');
    console.log('╠════════════════════════════════════════════════════════════╣');

    if (summary.durations['angular-bootstrap']) {
      console.log(`║  Angular Bootstrap:         ${summary.durations['angular-bootstrap'].toFixed(2).padStart(8)}ms  ║`);
    }
    if (summary.durations['auth-processing']) {
      console.log(`║  Auth Processing:           ${summary.durations['auth-processing'].toFixed(2).padStart(8)}ms  ║`);
    }

    // 🎯 PRIMARY METRIC: Time to Interactive
    if (summary.durations['time-to-interactive']) {
      console.log('╠════════════════════════════════════════════════════════════╣');
      console.log(`║  ⚡ TIME TO INTERACTIVE:    ${summary.durations['time-to-interactive'].toFixed(2).padStart(8)}ms  ║`);

      const tti = summary.durations['time-to-interactive'];
      const target = 100; // <100ms target
      const status = tti < target ? '✅ MEETS TARGET' : '❌ NEEDS WORK';
      const percentage = ((tti - target) / target * 100).toFixed(0);

      console.log(`║  Target: <100ms             Status: ${status.padEnd(19)} ║`);
      if (tti >= target) {
        console.log(`║  ${Math.abs(parseFloat(percentage))}% over target${' '.repeat(44)} ║`);
      }
      console.log(`║  ℹ️  UI is interactive - user can view messages & history  ║`);
    }

    // Background metrics (don't block user)
    if (summary.durations['websocket-connection']) {
      console.log('╠════════════════════════════════════════════════════════════╣');
      console.log(`║  WebSocket (background):    ${summary.durations['websocket-connection'].toFixed(2).padStart(8)}ms  ║`);
      console.log(`║  ℹ️  Connects in background - doesn't block UI             ║`);
    }
    if (summary.durations['total-connection-time']) {
      console.log(`║  Total (load to connected): ${summary.durations['total-connection-time'].toFixed(2).padStart(8)}ms  ║`);
    }
    console.log('╚════════════════════════════════════════════════════════════╝');
  }
}
